/**
 * Generate Node ID Utility
 *
 * Generates UUIDs for nodes created in offline mode.
 * These IDs are passed to the backend and used as the actual node IDs,
 * ensuring that offline-created nodes can be referenced by links/fibers
 * before syncing to the server.
 */

import { v4 as uuidv4 } from 'uuid';

/**
 * Generate a unique node ID for offline node creation.
 * This ID will be sent to the backend and used as the actual node ID.
 *
 * @returns A UUID string that can be used as a node ID
 *
 * @example
 * ```ts
 * const nodeId = generateNodeId();
 * // Use in mutation payload
 * createODF({
 *   id: nodeId,
 *   text: 'My ODF',
 *   ...
 * });
 *
 * // The same ID can be referenced by fibers created before sync
 * createFiber({
 *   from: { nodeId, portId: '01' },
 *   to: { nodeId: otherNodeId, portId: '02' },
 *   ...
 * });
 * ```
 */
export function generateNodeId(): string {
  return uuidv4();
}

/**
 * Check if a string is a valid UUID v4 format.
 * Useful for validating node IDs.
 *
 * @param id - The string to check
 * @returns true if the string is a valid UUID v4 format
 */
export function isValidNodeId(id: string): boolean {
  const uuidV4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidV4Regex.test(id);
}
